/**
 * Fuel cost per mile, as typed (DEC-326 item 3a) — pure, unit-tested.
 *
 * The office types dollars ("0.85" or "$0.85"); the API stores integer cents. The
 * conversion is the shared exact parser, never a float multiply (money-is-integer-cents:
 * `Math.round(1.005 * 100)` is 100). Blank means "not set".
 */

import { formatCentsAsDollars, parseDollarsToCents } from '@lodex-pro/shared/money';

/** The API's ceiling ($1,000.00 per mile); it only catches a typo. */
export const MAX_FUEL_CENTS_PER_MILE = 100_000;

export type FuelRateParse =
  | { readonly ok: true; readonly cents: number | null }
  | { readonly ok: false; readonly message: string };

export function parseFuelRate(raw: string): FuelRateParse {
  const p = parseDollarsToCents(raw);
  if (!p.ok) {
    if (p.reason === 'blank') return { ok: true, cents: null };
    return { ok: false, message: 'Enter a dollar amount, like 0.85.' };
  }
  if (p.cents < 0) return { ok: false, message: 'The rate cannot be negative.' };
  if (p.cents > MAX_FUEL_CENTS_PER_MILE) {
    return { ok: false, message: 'That is over $1,000 a mile — check the number.' };
  }
  return { ok: true, cents: p.cents };
}

/** "0.85" for the input box; '' when not set. */
export function fuelRateToInput(cents: number | null): string {
  return cents === null ? '' : formatCentsAsDollars(cents);
}
